import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Users, Route as RouteIcon, TrendingUp, Leaf } from 'lucide-react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { requireLevel } from '@/lib/guards';
import { useCompany } from '@/lib/company-context';
import { useCompanyDashboard } from '@/lib/queries/corporate';
import { StatCard } from '@/components/shared/stat-card';
import { StatCardSkeleton } from '@/components/shared/skeleton';
import { formatNumber, formatDistance, formatCO2 } from '@/lib/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/app/dashboard')({
  beforeLoad: requireLevel(5),
  component: DashboardPage,
});

const dayOptions = [
  { value: 7, label: '7d' },
  { value: 14, label: '14d' },
  { value: 30, label: '30d' },
] as const;

function DashboardPage() {
  const { company } = useCompany();
  const [days, setDays] = useState(14);
  const { data, isLoading } = useCompanyDashboard(company.slug, days);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
        <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
          {dayOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded-md transition-colors',
                days === opt.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Strip */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
      ) : data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Active Members"
            value={data.active_members}
            subtitle={`last ${days} days`}
          />
          <StatCard
            icon={RouteIcon}
            label="Total Trips"
            value={formatNumber(data.stats.total_trips)}
          />
          <StatCard
            icon={TrendingUp}
            label="Total Points"
            value={formatNumber(data.stats.total_points)}
          />
          <StatCard
            icon={Leaf}
            label="CO\u2082 Avoided"
            value={formatCO2(data.carbon.avoided_emissions_g)}
          />
        </div>
      ) : null}

      {/* Activity Trend */}
      {data?.activity_trend && data.activity_trend.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-base font-semibold text-foreground mb-4">Activity Trend</h2>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.activity_trend}>
                <defs>
                  <linearGradient id="tripsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  stroke="var(--muted-foreground)"
                  tickFormatter={(d: string) =>
                    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="trips"
                  stroke="var(--primary)"
                  fill="url(#tripsFill)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="green_trips"
                  stroke="var(--chart-walk)"
                  fill="none"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Transport Modes + Carbon */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-base font-semibold text-foreground mb-4">Transport Modes</h2>
            <ModalSplitBars modalSplit={data.modal_split} />
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-base font-semibold text-foreground mb-4">Carbon Impact</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">CO\u2082 Avoided</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {formatCO2(data.carbon.avoided_emissions_g)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Actual Emissions</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCO2(data.carbon.actual_emissions_g)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Green Trips</span>
                <span className="text-sm font-medium text-foreground">
                  {data.carbon.trips_count}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Distance</span>
                <span className="text-sm font-medium text-foreground">
                  {formatDistance(data.stats.total_distance_meters)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ModalSplitBarsProps {
  modalSplit: {
    walk_m: number;
    run_m: number;
    cycle_m: number;
    metro_m: number;
    bus_m: number;
    train_m: number;
    drive_m: number;
  };
}

const modes = [
  { key: 'walk_m', label: 'Walk', color: 'bg-[var(--chart-walk)]' },
  { key: 'run_m', label: 'Run', color: 'bg-[var(--chart-run)]' },
  { key: 'cycle_m', label: 'Cycle', color: 'bg-[var(--chart-cycle)]' },
  { key: 'metro_m', label: 'Metro', color: 'bg-[var(--chart-metro)]' },
  { key: 'bus_m', label: 'Bus', color: 'bg-[var(--chart-bus)]' },
  { key: 'train_m', label: 'Train', color: 'bg-[var(--chart-train)]' },
  { key: 'drive_m', label: 'Drive', color: 'bg-[var(--chart-drive)]' },
] as const;

function ModalSplitBars({ modalSplit }: ModalSplitBarsProps) {
  const total = modes.reduce((sum, m) => sum + (modalSplit[m.key] || 0), 0);
  if (total === 0) {
    return <p className="text-sm text-muted-foreground">No trip data yet.</p>;
  }

  return (
    <div className="space-y-2.5">
      {modes
        .filter((m) => modalSplit[m.key] > 0)
        .sort((a, b) => modalSplit[b.key] - modalSplit[a.key])
        .map((m) => {
          const pct = (modalSplit[m.key] / total) * 100;
          return (
            <div key={m.key} className="flex items-center gap-3">
              <span className="w-12 text-xs text-muted-foreground">{m.label}</span>
              <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all', m.color)}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-10 text-right text-xs font-medium text-foreground tabular-nums">
                {Math.round(pct)}%
              </span>
            </div>
          );
        })}
    </div>
  );
}
